import { Button, Checkbox, Dropdown, Form, Input, Text } from '@ui/themed';
import { useCallback, useEffect, useState } from 'react';
import type { FormField } from '../types';

type ActionFormProps = {
  schema: FormField[];
  data: Record<string, unknown>;
  errors: Record<string, string[]>;
  saving: boolean;
  submitLabel?: string;
  onSave: (payload: Record<string, unknown>) => void;
};

export function ActionForm({ schema, data, errors, saving, submitLabel, onSave }: ActionFormProps) {
  const [input, setInput] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const initial: Record<string, unknown> = {};
    for (const field of schema) {
      initial[field.property] = data[field.property] ?? null;
    }
    setInput(initial);
  }, [schema, data]);

  const updateField = useCallback((property: string, value: unknown) => {
    setInput((prev) => ({ ...prev, [property]: value }));
  }, []);

  const conditionSatisfied = useCallback(
    (field: FormField): boolean => {
      const { condition } = field;
      if (condition === null) return true;

      let satisfied: boolean;
      switch (condition.type) {
        case 'any':
          satisfied = Array.isArray(condition.value)
            ? condition.value.includes(input[condition.on])
            : false;
          break;
        case 'equal':
          satisfied = condition.value === input[condition.on];
          break;
        default:
          satisfied = false;
      }

      if (!satisfied && input[field.property] !== null) {
        updateField(field.property, null);
      }

      return satisfied;
    },
    [input, updateField],
  );

  return (
    <Form onSubmit={() => onSave(input)}>
      {schema.map((field) => {
        if (!conditionSatisfied(field)) return null;

        return (
          <div key={field.property}>
            {field.type === 'checkbox' ? (
              <Checkbox
                name={field.property}
                label={field.label}
                checked={!!input[field.property]}
                onChange={() => updateField(field.property, !input[field.property])}
              />
            ) : field.type === 'select' ? (
              <Dropdown
                id={field.property}
                label={field.label}
                placeholder="Select..."
                value={(input[field.property] as string) ?? ''}
                options={
                  field.attributes.options?.map((option) => ({
                    value: option.value,
                    label: option.text,
                  })) ?? []
                }
                onChange={(value) => updateField(field.property, value)}
              />
            ) : (
              <Input
                name={field.property}
                label={field.label}
                description={field.description}
                type={field.type === 'date' ? 'date' : undefined}
                inputMode={field.type === 'number' ? 'numeric' : 'text'}
                value={(input[field.property] as string) ?? ''}
                onChange={(value) => updateField(field.property, value)}
              />
            )}

            {errors[field.property] && (
              <div className="mt-2 flex flex-col gap-1">
                {errors[field.property]?.map((error) => (
                  <Text key={error} size="sm">
                    <span className="text-red-600">{error}</span>
                  </Text>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <Button type="submit" disabled={saving}>
        {saving ? `${submitLabel ?? 'Save'}...` : (submitLabel ?? 'Save')}
      </Button>
    </Form>
  );
}
